import {DefaultValue, selector, selectorFamily, waitForAll} from 'recoil';

import {ServiceRequestService} from '../../api/services/serviceRequest.service';

import {get as _get} from 'lodash';
import {DocumentService} from '../../api/services/document.service';
import {IServiceRequest} from '../../shared/types/serviceRequest.types';
import {currentCompanyIdState, currentCompanyState} from '../company/atoms';
import {companyByIdSelectorFamily} from '../company/selectors';
import {currentDeviceTypeCodeState, deviceTypeMapWithNamesState} from '../deviceTypes/atoms';
import {filtersServiceRequestsState, filtersServiceRequestsToAssignState} from '../filters/atoms';
import {
  assignableServiceRequestsState,
  contentTreeFilesQueryTrigger,
  contentTreeQueryTrigger,
  currentServiceRequestState,
  formTemplatesState,
  serviceRequestPageable,
  serviceRequestToAssignPageable,
  serviceRequestsQueryTrigger,
  serviceRequestsSortQueryTrigger,
  serviceRequestsState,
  serviceRequestsToAssignQueryTrigger,
  serviceRequestsToAssignSortQueryTrigger,
  serviceRequestsToAssignState,
  toolbarServiceRequestSearchString,
  toolbarServiceRequestSearchStringArchived,
  toolbarServiceRequestToAssignSearchString,
  triggerReasonDelays,
} from './atoms';

export const serviceRequestsQuery = selector({
  key: 'serviceRequestsQuery',
  get: async ({get}) => {
    const [pageable, filters, searchString, mapDeviceType, sort, archived] = get(
      waitForAll([
        serviceRequestPageable,
        filtersServiceRequestsState,
        toolbarServiceRequestSearchString,
        deviceTypeMapWithNamesState,
        serviceRequestsSortQueryTrigger,
        toolbarServiceRequestSearchStringArchived,
        serviceRequestsQueryTrigger,
      ]),
    );

    const specBase = {archive: archived};

    const res = await ServiceRequestService.getAllList({
      spec:
        searchString.id && searchString.extNumber
          ? {id: searchString.id, extNumber: searchString.extNumber, ...specBase}
          : searchString.id
            ? {id: searchString.id, ...specBase}
            : searchString.extNumber
              ? {extNumber: searchString.extNumber, ...specBase}
              : specBase,
      pageable: {
        order: [sort?.[0]?.field || 'id', sort?.[0]?.sort === 'asc' ? 'ASC' : 'DESC'],
        ...pageable,
      },
      params: {
        ...Object.fromEntries(
          filters.map((i) => {
            if (i[0] === 'serviceType') {
              const found = mapDeviceType.get((i[1] as string).toLowerCase());

              return [i[0], (found && found) || i[1]];
            }

            return i.slice(0, 2);
          }),
        ),
      },
    });

    return res.data;
  },

  set: ({set}, newValue) => {
    set(serviceRequestsState, newValue);
  },
});

export const serviceRequestsToAssignQuery = selector({
  key: 'serviceRequestsToAssignQuery',
  get: async ({get}) => {
    get(serviceRequestsToAssignQueryTrigger);
    const [pageable, filters, id, mapDeviceType, sort] = get(
      waitForAll([
        serviceRequestToAssignPageable,
        filtersServiceRequestsToAssignState,
        toolbarServiceRequestToAssignSearchString,
        deviceTypeMapWithNamesState,
        serviceRequestsToAssignSortQueryTrigger,
      ]),
    );

    const res = await ServiceRequestService.getAllList({
      spec: {
        id: id || null,
      },
      pageable: {
        order: [sort?.[0]?.field || 'id', sort?.[0]?.sort === 'asc' ? 'ASC' : 'DESC'],
        ...pageable,
      },
      params: {
        status: ['CREATED', 'READY_TO_PROCESS', 'DELAYED'],
        ...Object.fromEntries(
          filters.map((i) => {
            if (i[0] === 'serviceType') {
              const found = mapDeviceType.get((i[1] as string).toLowerCase());

              return [i[0], (found && found) || i[1]];
            }

            return i.slice(0, 2);
          }),
        ),
      },
    });

    return res.data;
  },

  set: ({set}, newValue) => {
    set(serviceRequestsToAssignState, newValue);
  },
});

export const currentServiceRequestQuery = selectorFamily({
  key: 'currentServiceRequestQuery',
  get:
    (id) =>
    async ({get}) => {
      get(serviceRequestsQueryTrigger);

      const res = await ServiceRequestService.getById(Number(id));

      return res.data;
    },

  set:
    () =>
    ({get, set}, newValue) => {
      const company = !(newValue instanceof DefaultValue) && newValue
        ? get(companyByIdSelectorFamily(_get(newValue, 'companyId')))
        : null;
      if (company) {
        set(currentCompanyState, company);
        set(currentCompanyIdState, company.id);
      }
      set(currentServiceRequestState, newValue);
      if (!(newValue instanceof DefaultValue) && newValue) {
        set(currentDeviceTypeCodeState, newValue.serviceType?.code as string);
      }
    },
});

export const serviceRequestIdsToAssign = selector({
  key: 'assignableServiceRequestIds',
  get: ({get}) => {
    const serviceRequests = get(assignableServiceRequestsState);

    return Object.entries(serviceRequests).reduce<number[]>((prev, cur) => {
      return [...prev, ...cur[1]];
    }, []);
  },
});

export const totalServiceRequestsToAssign = selector({
  key: 'totalServiceRequestsToAssign',
  get: ({get}) => {
    const serviceRequests = get(assignableServiceRequestsState);

    return Object.entries(serviceRequests).reduce<number>((prev, cur) => prev + cur[1].length, 0);
  },
});

export const serviceRequestStatusesQuery = selectorFamily({
  key: 'serviceRequestStatusesQuery',
  get:
    (id) =>
    async ({get}) => {
      get(serviceRequestsQueryTrigger);

      const res = await ServiceRequestService.getStatusHistory(Number(id));

      return res.data;
    },
});

export const contentTreeQueryFamily = selectorFamily({
  key: 'contentTreeQueryFamily',
  get:
    (id) =>
    async ({get}) => {
      get(serviceRequestsQueryTrigger);
      get(contentTreeQueryTrigger);
      const res = await ServiceRequestService.getContentTree(Number(id));

      return res.data;
    },
  set:
    () =>
    ({set, get}, newValue) => {
      if (!(newValue instanceof DefaultValue) && newValue) {
        const serviceRequest = get(currentServiceRequestState) as IServiceRequest | null;

        if (!serviceRequest) {
          return;
        }

        set(formTemplatesState, (prev) => ({
          ...prev,
          [(serviceRequest.serviceType?.code as string) + serviceRequest.id]: newValue,
        }));
      }
    },
});

export const contentTreeFilesQueryFamily = selectorFamily({
  key: 'contentTreeFilesQueryFamily',
  get:
    (ids: string[] | null) =>
    async ({get}) => {
      if (!ids) return null;

      get(contentTreeFilesQueryTrigger);

      const docPromises = ids.map((id) => DocumentService.getContent(id));
      const res = await Promise.allSettled(docPromises);
      const blobPromises = res.map((item) => {
        if (item.status === 'fulfilled') {
          return new Promise<Blob | null>((res) => {
            res(item.value.data);
          });
        } else {
          return Promise.resolve(null);
        }
      });
      const blobs = await Promise.all(blobPromises);
      const urls = blobs.map((blob) => (blob ? URL.createObjectURL(blob) : ''));
      return urls;
    },
});

export const contentTreePhotoQueryFamily = selectorFamily({
  key: 'contentTreePhotoQueryFamily',
  get: (id: string | null) => async () => {
    if (!id) return null;

    const res = await DocumentService.getContent(id);

    const url = URL.createObjectURL(res.data);

    return url;
  },
});

export const serviceRequestsReportQuery = selector({
  key: 'serviceRequestsReportQuery',
  get: async () => {
    const res = await ServiceRequestService.getReport();
    return res.data;
  },
});

export const reasonDelaysFamilyQuery = selectorFamily({
  key: 'reasonDelaysFamilyQuery',
  get:
    (id) =>
    async ({get}) => {
      get(serviceRequestsQueryTrigger);
      get(triggerReasonDelays);

      if (!id) return null;

      const res = await ServiceRequestService.getReasonDelays(Number(id));

      return res.data;
    },
});
